import posthog from "posthog-js";
import { useEffect } from "react";

import { useAppAuth } from "@/auth/use-app-auth";
import { useAuthProfile } from "@/hooks/useAuthProfile";
import { getFirstTouch, getHandedOffDevice } from "./attribution";
import { firstTouchProperties } from "./first-touch";
import { ga4SetUser } from "./ga4";

/**
 * Ties analytics identity to the local Recruiter row once the recruiter's profile has loaded:
 *   - PostHog: identify(Recruiter.id) with email/name as person properties (never event props);
 *     first-touch attribution set once on the person; a marketing-site anonymous id handed over
 *     on a CTA is aliased into the person once, merging pre-signup activity on tryapex25.com.
 *   - GA4: user_id = Recruiter.id.
 * Renders nothing.
 */
export const AnalyticsIdentity = () => {
  const { isAuthenticated } = useAppAuth();
  const { data: profile } = useAuthProfile(isAuthenticated);
  const recruiterId: string | undefined = profile?.id;

  useEffect(() => {
    if (!recruiterId) return;
    const touch = getFirstTouch();

    try {
      if (posthog.__loaded) {
        posthog.identify(
          recruiterId,
          { email: profile?.email, name: profile?.name },
          touch ? firstTouchProperties(touch) : undefined
        );

        const handedOff = getHandedOffDevice();
        const aliasedKey = `first_touch_device_linked:${recruiterId}`;
        if (handedOff && handedOff !== recruiterId && !localStorage.getItem(aliasedKey)) {
          posthog.alias(handedOff, recruiterId);
          localStorage.setItem(aliasedKey, "1");
        }
      }
    } catch {
      // Analytics must never break the app.
    }
    ga4SetUser(recruiterId);
  }, [recruiterId, profile?.email, profile?.name]);

  return null;
};
